<div class="community-moderation container-xl py-3" *transloco="let t">
  <header class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
    <div>
      <h1 class="h3 mb-0">🛡 {{ t('community.moderation.title') }}</h1>
      <p class="text-muted small mb-0">{{ t('community.moderation.subtitle') }}</p>
    </div>
    <a class="btn btn-outline-secondary btn-sm" [routerLink]="communityLink()">← {{ t('community.thread.back') }}</a>
  </header>

  @if (loading() && !forbidden()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else if (forbidden()) {
    <div class="alert alert-warning">{{ t('community.moderation.forbidden') }}</div>
  } @else {
    @if (error(); as key) {
      <div class="alert alert-danger d-flex justify-content-between align-items-start">
        <span>{{ t(key) }}</span>
        <button type="button" class="btn-close" (click)="clearError()"></button>
      </div>
    }

    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'reports'" (click)="selectTab('reports')">
          {{ t('community.moderation.tabs.reports') }}
          @if (openCount(); as n) { <span class="badge bg-danger ms-1">{{ n }}</span> }
        </button>
      </li>
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'queue'" (click)="selectTab('queue')">
          {{ t('community.moderation.tabs.queue') }}
          @if (queueCount() > 0) { <span class="badge bg-warning text-dark ms-1">{{ queueCount() }}</span> }
        </button>
      </li>
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'protocol'" (click)="selectTab('protocol')">
          {{ t('community.moderation.tabs.protocol') }}
        </button>
      </li>
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="tab() === 'sanction'" (click)="selectTab('sanction'); resetSanctionForm()">
          {{ t('community.moderation.tabs.sanction') }}
        </button>
      </li>
    </ul>

    <!-- ================================================================== -->
    <!-- Reports                                                             -->
    <!-- ================================================================== -->
    @if (tab() === 'reports') {
      <p class="text-muted small">{{ t('community.moderation.reports.hint') }}</p>
      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-4">
          <label class="form-label small mb-0">{{ t('community.moderation.reports.status') }}</label>
          <select class="form-select form-select-sm" [ngModel]="reportStatus()" (ngModelChange)="onReportStatusChange($event)">
            <option value="">{{ t('community.moderation.reports.status_open_or_review') }}</option>
            @for (st of reportStatuses; track st) {
              <option [value]="st">{{ t('community.moderation.reports.status_' + st) }}</option>
            }
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label small mb-0">{{ t('community.channel.label') }}</label>
          <select class="form-select form-select-sm" [ngModel]="reportChannelId() ?? ''" (ngModelChange)="onReportChannelChange($event)">
            <option value="">{{ t('community.moderation.all_channels') }}</option>
            @for (x of moderatedChannels(); track x.channel.id) {
              <option [ngValue]="x.channel.id">{{ x.group.name }} / # {{ x.channel.name }}</option>
            }
          </select>
        </div>
      </div>

      @if (reportsLoading()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (reports().length === 0) {
        <p class="text-muted">{{ t('community.moderation.reports.empty') }}</p>
      } @else {
        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>{{ t('community.moderation.reports.content') }}</th>
                <th>{{ t('community.moderation.reports.reason') }}</th>
                <th>{{ t('community.moderation.reports.reporter') }}</th>
                <th>{{ t('community.moderation.reports.status') }}</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              @for (r of reports(); track r.id) {
                <tr>
                  <td class="community-moderation__excerpt">
                    <div class="d-flex flex-wrap gap-1 mb-1">
                      <span class="badge bg-light text-dark">{{ t(r.reportable_type === 'CommunityThread' ? 'community.moderation.type_thread' : 'community.moderation.type_post') }}</span>
                      <span class="badge bg-info text-dark"># {{ r.channel.name }}</span>
                      <small class="text-muted">{{ r.created_at | date:'short' }}</small>
                    </div>
                    @if (threadLink(r.thread_id); as link) {
                      <a [routerLink]="link">{{ r.excerpt || t('community.moderation.reports.no_excerpt') }}</a>
                    } @else {
                      <span class="text-muted">{{ r.excerpt || t('community.moderation.reports.no_excerpt') }}</span>
                    }
                    @if (r.author) {
                      <div class="small text-muted">
                        {{ t('community.moderation.reports.author') }}:
                        <button type="button" class="btn btn-link btn-sm community-moderation__link-btn" (click)="openSummary(r.author.id)">{{ userName(r.author) }}</button>
                      </div>
                    }
                  </td>
                  <td class="community-moderation__reason small">
                    {{ r.reason }}
                    @if (r.resolution) {
                      <div class="text-muted mt-1"><em>{{ t('community.moderation.reports.resolution') }}:</em> {{ r.resolution }}</div>
                    }
                  </td>
                  <td class="small">{{ userName(r.reporter) }}</td>
                  <td>
                    <span class="badge" [class]="'badge ' + reportStatusClass(r.status)">{{ t('community.moderation.reports.status_' + r.status) }}</span>
                    @if (r.assignee) {
                      <div class="small text-muted">{{ t('community.moderation.reports.assignee') }}: {{ isMine(r) ? t('community.moderation.reports.me') : userName(r.assignee) }}</div>
                    }
                    @if (r.resolved_at) {
                      <div class="small text-muted">{{ r.resolved_at | date:'short' }} · {{ userName(r.resolved_by) }}</div>
                    }
                  </td>
                  <td class="text-end text-nowrap">
                    @if (r.status === 'open' || r.status === 'in_review') {
                      @if (!isMine(r)) {
                        <button type="button" class="btn btn-sm btn-outline-secondary mb-1" [disabled]="busy()" (click)="assignToMe(r)">
                          {{ t('community.moderation.reports.assign_me') }}
                        </button>
                      }
                      <br />
                      <button type="button" class="btn btn-sm btn-outline-success mb-1" [disabled]="busy()" (click)="openClose(r, 'resolved')">
                        {{ t('community.moderation.reports.resolve') }}
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary mb-1" [disabled]="busy()" (click)="openClose(r, 'dismissed')">
                        {{ t('community.moderation.reports.dismiss') }}
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <small class="text-muted">{{ t('community.moderation.total', { count: reportsTotal() }) }}</small>
          @if (reportsTotalPages() > 1) {
            <nav class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="reportsPage() <= 1" (click)="loadReports(reportsPage() - 1)">‹</button>
              <span class="align-self-center small text-muted">{{ reportsPage() }} / {{ reportsTotalPages() }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="reportsPage() >= reportsTotalPages()" (click)="loadReports(reportsPage() + 1)">›</button>
            </nav>
          }
        </div>
      }
    }

    <!-- ================================================================== -->
    <!-- Approval queue                                                      -->
    <!-- ================================================================== -->
    @if (tab() === 'queue') {
      <p class="text-muted small">{{ t('community.moderation.queue.hint') }}</p>
      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-4">
          <label class="form-label small mb-0">{{ t('community.channel.label') }}</label>
          <select class="form-select form-select-sm" [ngModel]="queueChannelId() ?? ''" (ngModelChange)="onQueueChannelChange($event)">
            <option value="">{{ t('community.moderation.all_channels') }}</option>
            @for (x of moderatedChannels(); track x.channel.id) {
              <option [ngValue]="x.channel.id">{{ x.group.name }} / # {{ x.channel.name }}</option>
            }
          </select>
        </div>
        <div class="col-md-8 text-md-end">
          <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="queueLoading()" (click)="loadQueue()">↻ {{ t('community.moderation.refresh') }}</button>
        </div>
      </div>

      @if (queueLoading()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (queueCount() === 0) {
        <p class="text-muted">{{ t('community.moderation.queue.empty') }}</p>
      } @else {
        @for (item of queueThreads(); track item.id) {
          <div class="card mb-2">
            <div class="card-body py-2">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span class="badge bg-light text-dark">{{ t('community.moderation.type_thread') }}</span>
                <span class="badge bg-info text-dark"># {{ item.channel.name }}</span>
                <strong>{{ item.title }}</strong>
                <small class="text-muted ms-auto">
                  <button type="button" class="btn btn-link btn-sm community-moderation__link-btn" (click)="openSummary(item.user?.id)">{{ userName(item.user) }}</button>
                  · {{ item.created_at | date:'medium' }}
                </small>
              </div>
              @if (item.flagged_reason) {
                <div class="small text-warning-emphasis">⚑ {{ t('community.moderation.queue.flagged', { reason: item.flagged_reason }) }}</div>
              }
              <div class="community-moderation__body small">
                {{ expanded().has('thread:' + item.id) ? preview(item.body, 100000) : preview(item.body) }}
              </div>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <button type="button" class="btn btn-sm btn-success" [disabled]="busy()" (click)="approve('thread', item.id)">✓ {{ t('community.moderation.queue.approve') }}</button>
                <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="openReject('thread', item.id)">✕ {{ t('community.moderation.queue.reject') }}</button>
                @if (item.body && item.body.length > 300) {
                  <button type="button" class="btn btn-sm btn-link" (click)="toggleExpanded('thread:' + item.id)">
                    {{ expanded().has('thread:' + item.id) ? t('community.moderation.queue.show_less') : t('community.moderation.queue.show_more') }}
                  </button>
                }
              </div>
            </div>
          </div>
        }
        @for (item of queuePosts(); track item.id) {
          <div class="card mb-2">
            <div class="card-body py-2">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span class="badge bg-light text-dark">{{ t('community.moderation.type_post') }}</span>
                <span class="badge bg-info text-dark"># {{ item.channel.name }}</span>
                <a [routerLink]="threadLink(item.thread_id)">{{ item.thread_title }}</a>
                <small class="text-muted ms-auto">
                  <button type="button" class="btn btn-link btn-sm community-moderation__link-btn" (click)="openSummary(item.user?.id)">{{ userName(item.user) }}</button>
                  · {{ item.created_at | date:'medium' }}
                </small>
              </div>
              @if (item.flagged_reason) {
                <div class="small text-warning-emphasis">⚑ {{ t('community.moderation.queue.flagged', { reason: item.flagged_reason }) }}</div>
              }
              <div class="community-moderation__body small">
                {{ expanded().has('post:' + item.id) ? preview(item.body, 100000) : preview(item.body) }}
              </div>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <button type="button" class="btn btn-sm btn-success" [disabled]="busy()" (click)="approve('post', item.id)">✓ {{ t('community.moderation.queue.approve') }}</button>
                <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="busy()" (click)="openReject('post', item.id)">✕ {{ t('community.moderation.queue.reject') }}</button>
                @if (item.body && item.body.length > 300) {
                  <button type="button" class="btn btn-sm btn-link" (click)="toggleExpanded('post:' + item.id)">
                    {{ expanded().has('post:' + item.id) ? t('community.moderation.queue.show_less') : t('community.moderation.queue.show_more') }}
                  </button>
                }
              </div>
            </div>
          </div>
        }
      }
    }

    <!-- ================================================================== -->
    <!-- Protocol                                                            -->
    <!-- ================================================================== -->
    @if (tab() === 'protocol') {
      <p class="text-muted small">{{ t('community.moderation.protocol.hint') }}</p>
      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-3">
          <label class="form-label small mb-0">{{ t('community.moderation.protocol.kind') }}</label>
          <select class="form-select form-select-sm" [ngModel]="protocolKind()" (ngModelChange)="onProtocolKindChange($event)">
            <option value="">{{ t('community.moderation.protocol.all_kinds') }}</option>
            @for (k of protocolKinds; track k) {
              <option [value]="k">{{ t('community.moderation.kind.' + k) }}</option>
            }
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-0">{{ t('community.moderation.protocol.user_id') }}</label>
          <input type="text" class="form-control form-control-sm" inputmode="numeric" [ngModel]="protocolUserId" (ngModelChange)="onProtocolUserChange($event)" />
        </div>
        <div class="col-md-6 text-md-end">
          <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="actionsLoading()" (click)="loadActions(actionsPage())">↻ {{ t('community.moderation.refresh') }}</button>
        </div>
      </div>

      @if (actionsLoading()) {
        <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
      } @else if (actions().length === 0) {
        <p class="text-muted">{{ t('community.moderation.protocol.empty') }}</p>
      } @else {
        <div class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>{{ t('community.moderation.protocol.date') }}</th>
                <th>{{ t('community.moderation.protocol.kind') }}</th>
                <th>{{ t('community.moderation.protocol.member') }}</th>
                <th>{{ t('community.moderation.protocol.scope') }}</th>
                <th>{{ t('community.moderation.reason') }}</th>
                <th>{{ t('community.moderation.protocol.actor') }}</th>
                <th>{{ t('community.moderation.protocol.state') }}</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              @for (a of actions(); track a.id) {
                <tr>
                  <td class="small text-nowrap">{{ a.created_at | date:'short' }}</td>
                  <td>
                    <span class="badge" [class]="'badge ' + kindClass(a.kind)">{{ t('community.moderation.kind.' + a.kind) }}</span>
                    @if (a.lifts_action_id) { <small class="text-muted d-block">#{{ a.lifts_action_id }}</small> }
                  </td>
                  <td class="small">
                    <button type="button" class="btn btn-link btn-sm community-moderation__link-btn" (click)="openSummary(a.user?.id)">{{ userName(a.user) }}</button>
                    @if (a.user) { <small class="text-muted d-block">#{{ a.user.id }}</small> }
                  </td>
                  <td class="small">{{ a.scope_name || t('community.moderation.scope_network') }}</td>
                  <td class="community-moderation__reason small">
                    {{ a.reason }}
                    @if (a.until) { <div class="text-muted">{{ t('community.moderation.until_label') }} {{ a.until | date:'medium' }}</div> }
                  </td>
                  <td class="small">{{ userName(a.actor) }}</td>
                  <td>
                    @switch (actionState(a)) {
                      @case ('active') { <span class="badge bg-danger">{{ t('community.moderation.protocol.state_active') }}</span> }
                      @case ('lifted') { <span class="badge bg-success">{{ t('community.moderation.protocol.state_lifted') }}</span> }
                      @case ('expired') { <span class="badge bg-secondary">{{ t('community.moderation.protocol.state_expired') }}</span> }
                    }
                  </td>
                  <td class="text-end">
                    @if (canLift(a)) {
                      <button type="button" class="btn btn-sm btn-outline-success" [disabled]="busy()" (click)="openLift(a)">{{ t('community.moderation.lift') }}</button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <small class="text-muted">{{ t('community.moderation.total', { count: actionsTotal() }) }}</small>
          @if (actionsTotalPages() > 1) {
            <nav class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="actionsPage() <= 1" (click)="loadActions(actionsPage() - 1)">‹</button>
              <span class="align-self-center small text-muted">{{ actionsPage() }} / {{ actionsTotalPages() }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="actionsPage() >= actionsTotalPages()" (click)="loadActions(actionsPage() + 1)">›</button>
            </nav>
          }
        </div>
      }
    }

    <!-- ================================================================== -->
    <!-- Sanction a member                                                   -->
    <!-- ================================================================== -->
    @if (tab() === 'sanction') {
      <p class="text-muted small">{{ t('community.moderation.sanction.hint') }}</p>

      @if (sanctionCreated(); as created) {
        <div class="alert alert-success d-flex justify-content-between align-items-start">
          <span>
            {{ t('community.moderation.sanction.created', { kind: t('community.moderation.kind.' + created.kind), name: userName(created.user) }) }}
            <button type="button" class="btn btn-link btn-sm community-moderation__link-btn ms-1" (click)="showProtocolFor(created.user!.id)">{{ t('community.moderation.sanction.show_protocol') }}</button>
          </span>
          <button type="button" class="btn-close" (click)="sanctionCreated.set(null)"></button>
        </div>
      }
      @if (sanctionError(); as key) {
        <div class="alert alert-danger">
          {{ t(key) }}
          @if (sanctionErrorDetail(); as detail) { <br /><small>{{ detail }}</small> }
        </div>
      }

      <div class="card">
        <div class="card-body">
          <div class="row g-2">
            <div class="col-md-6 position-relative">
              @if (isAdmin()) {
                <label class="form-label small mb-0">{{ t('community.admin.grants.user_search') }}</label>
                <input type="text" class="form-control form-control-sm" autocomplete="off"
                       [placeholder]="t('community.admin.grants.user_search_placeholder')"
                       [ngModel]="userSearchTerm" (ngModelChange)="onUserSearch($event)" />
                @if (userResults().length > 0) {
                  <ul class="list-group community-moderation__user-results">
                    @for (u of userResults(); track u.id) {
                      <li class="list-group-item list-group-item-action py-1 small" role="button" (click)="pickUser(u)">
                        <strong>#{{ u.id }}</strong> {{ userLabel(u) }}
                      </li>
                    }
                  </ul>
                }
                @if (sanctionDraft.user; as picked) {
                  <div class="form-text">✓ #{{ picked.id }} {{ userLabel(picked) }}</div>
                } @else {
                  <div class="form-text">{{ t('community.admin.grants.user_search_hint') }}</div>
                }
              } @else {
                <!-- Moderators may not search users (403); they enter the id (shown next to every author for admins, in the protocol for everyone). -->
                <label class="form-label small mb-0">{{ t('community.admin.grants.user_id') }}</label>
                <input type="number" class="form-control form-control-sm" min="1" [(ngModel)]="sanctionDraft.user_id" />
                <div class="form-text">{{ t('community.moderation.sanction.user_id_hint') }}</div>
              }
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-0">{{ t('community.moderation.sanction.kind') }}</label>
              <select class="form-select form-select-sm" [ngModel]="sanctionDraft.kind" (ngModelChange)="onKindChange($event)">
                @for (k of sanctionKinds; track k) {
                  @if (k !== 'hide' || isAdmin()) {
                    <option [value]="k">{{ t('community.moderation.kind.' + k) }}</option>
                  }
                }
              </select>
              <div class="form-text">{{ t('community.moderation.kind_hint.' + sanctionDraft.kind) }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-0">{{ t('community.moderation.sanction.until') }}</label>
              <input type="datetime-local" class="form-control form-control-sm" [(ngModel)]="sanctionDraft.until" />
              <div class="form-text">{{ t('community.moderation.sanction.until_hint') }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-0">{{ t('community.moderation.sanction.scope') }}</label>
              <select class="form-select form-select-sm" [ngModel]="sanctionDraft.scope" (ngModelChange)="onScopeChange($event)" [disabled]="sanctionDraft.kind === 'hide'">
                @if (isAdmin()) {
                  <option value="network">{{ t('community.moderation.scope_network') }}</option>
                }
                @if (moderatedGroups().length > 0) {
                  <option value="group">{{ t('community.admin.grants.scope_group') }}</option>
                }
                <option value="channel">{{ t('community.admin.grants.scope_channel') }}</option>
              </select>
            </div>
            @if (sanctionDraft.scope === 'group') {
              <div class="col-md-5">
                <label class="form-label small mb-0">{{ t('community.admin.grants.scope_group') }}</label>
                <select class="form-select form-select-sm" [(ngModel)]="sanctionDraft.scope_id">
                  @for (g of moderatedGroups(); track g.id) {
                    <option [ngValue]="g.id">{{ g.name }}</option>
                  }
                </select>
              </div>
            } @else if (sanctionDraft.scope === 'channel') {
              <div class="col-md-5">
                <label class="form-label small mb-0">{{ t('community.admin.grants.scope_channel') }}</label>
                <select class="form-select form-select-sm" [(ngModel)]="sanctionDraft.scope_id">
                  @for (x of moderatedChannels(); track x.channel.id) {
                    <option [ngValue]="x.channel.id">{{ x.group.name }} / # {{ x.channel.name }}</option>
                  }
                </select>
              </div>
            }
            <div class="col-12">
              <label class="form-label small mb-0">{{ t('community.moderation.reason') }} *</label>
              <textarea class="form-control form-control-sm" rows="3" maxlength="2000"
                        [placeholder]="t('community.moderation.reason_placeholder')" [(ngModel)]="sanctionDraft.reason"></textarea>
              <div class="form-text">{{ t('community.moderation.sanction.reason_hint') }}</div>
            </div>
            <div class="col-12 d-flex gap-2">
              <button type="button" class="btn btn-sm btn-danger" [disabled]="busy() || !sanctionDraftValid()" (click)="submitSanction()">
                {{ t('community.moderation.sanction.submit') }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="busy()" (click)="resetSanctionForm()">
                {{ t('community.threads.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  }

  <!-- Dialogs -->
  @if (closeTarget(); as target) {
    <app-community-reason-modal [titleKey]="target.status === 'resolved' ? 'community.moderation.reports.resolve_title' : 'community.moderation.reports.dismiss_title'"
                                hintKey="community.moderation.reports.resolution_hint"
                                labelKey="community.moderation.reports.resolution"
                                [placeholderKey]="null"
                                [confirmKey]="target.status === 'resolved' ? 'community.moderation.reports.resolve' : 'community.moderation.reports.dismiss'"
                                [confirmClass]="target.status === 'resolved' ? 'btn-success' : 'btn-secondary'"
                                [busy]="busy()"
                                [error]="closeError()"
                                (confirmed)="confirmClose($event)"
                                (cancelled)="closeTarget.set(null)" />
  }

  @if (rejectTarget(); as target) {
    <app-community-reason-modal titleKey="community.moderation.queue.reject_title"
                                hintKey="community.moderation.queue.reject_hint"
                                confirmKey="community.moderation.queue.reject"
                                confirmClass="btn-danger"
                                [optional]="true"
                                [busy]="busy()"
                                [error]="rejectError()"
                                (confirmed)="confirmReject($event)"
                                (cancelled)="rejectTarget.set(null)" />
  }

  @if (liftTarget(); as target) {
    <app-community-reason-modal titleKey="community.moderation.lift_title"
                                [titleParams]="{ kind: t('community.moderation.kind.' + target.kind), name: userName(target.user) }"
                                confirmKey="community.moderation.lift"
                                confirmClass="btn-success"
                                [busy]="busy()"
                                [error]="liftError()"
                                (confirmed)="confirmLift($event)"
                                (cancelled)="liftTarget.set(null)" />
  }

  @if (summaryUserId(); as userId) {
    <app-community-member-summary [userId]="userId" (closed)="summaryUserId.set(null)" (changed)="onSummaryChanged()" />
  }
</div>
